import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { InventoryCategory, InventoryGroup } from '../types/inventory';
import {
    getCategories,
    getGroups,
    createCategory,
    updateCategory,
    deleteCategory,
    createGroup,
    updateGroup,
    deleteGroup
} from '../services/inventoryApi';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import { Link } from 'react-router-dom';

interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (name: string) => void;
    initialName?: string;
    title: string;
}

interface GroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (categoryId: number, name: string) => void;
    categories: InventoryCategory[];
    initialData?: {
        categoryId: number;
        name: string;
    };
    title: string;
}

function CategoryModal({ isOpen, onClose, onSubmit, initialName = '', title }: CategoryModalProps) {
    const [name, setName] = useState(initialName);

    useEffect(() => {
        setName(initialName);
    }, [initialName]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(name);
        setName('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold mb-4">{title}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Category Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            required
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function GroupModal({ isOpen, onClose, onSubmit, categories, initialData, title }: GroupModalProps) {
    const [categoryId, setCategoryId] = useState(initialData?.categoryId || categories[0]?.id || 0);
    const [name, setName] = useState(initialData?.name || '');

    useEffect(() => {
        if (initialData) {
            setCategoryId(initialData.categoryId);
            setName(initialData.name);
        } else {
            setCategoryId(categories[0]?.id || 0);
            setName('');
        }
    }, [initialData, categories]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(categoryId, name);
        setName('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold mb-4">{title}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <select
                            value={categoryId}
                            onChange={(e) => setCategoryId(Number(e.target.value))}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            required
                        >
                            <option value="">Select a category</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Group Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            required
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function InventoryManagePage() {
    const [categories, setCategories] = useState<InventoryCategory[]>([]);
    const [groups, setGroups] = useState<InventoryGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Category Modal States
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null);
    const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);

    // Group Modal States
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<InventoryGroup | null>(null);
    const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [categoriesRes, groupsRes] = await Promise.all([
                getCategories(),
                getGroups()
            ]);
            setCategories(categoriesRes.data.data);
            setGroups(groupsRes.data.data);
        } catch (error) {
            setError('Failed to fetch data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Category Handlers
    const handleCreateCategory = async (name: string) => {
        try {
            await createCategory(name);
            await fetchData();
            setIsCategoryModalOpen(false);
        } catch (error) {
            setError('Failed to create category');
        }
    };

    const handleUpdateCategory = async (name: string) => {
        if (!selectedCategory) return;
        try {
            await updateCategory(selectedCategory.id, name);
            await fetchData();
            setIsEditCategoryModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            setError('Failed to update category');
        }
    };

    const handleDeleteCategory = async () => {
        if (!selectedCategory) return;
        try {
            await deleteCategory(selectedCategory.id);
            await fetchData();
            setIsDeleteCategoryModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            setError('Failed to delete category');
        }
    };

    // Group Handlers
    const handleCreateGroup = async (categoryId: number, name: string) => {
        try {
            await createGroup(categoryId, name);
            await fetchData();
            setIsGroupModalOpen(false);
        } catch (error) {
            setError('Failed to create group');
        }
    };

    const handleUpdateGroup = async (categoryId: number, name: string) => {
        if (!selectedGroup) return;
        try {
            await updateGroup(selectedGroup.id, categoryId, name);
            await fetchData();
            setIsEditGroupModalOpen(false);
            setSelectedGroup(null);
        } catch (error) {
            setError('Failed to update group');
        }
    };

    const handleDeleteGroup = async () => {
        if (!selectedGroup) return;
        try {
            await deleteGroup(selectedGroup.id);
            await fetchData();
            setIsDeleteGroupModalOpen(false);
            setSelectedGroup(null);
        } catch (error) {
            setError('Failed to delete group');
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    if (error) {
        return <div className="text-red-600">{error}</div>;
    }

    return (
        <div className="space-y-8">
            <header className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <Link
                        to="/inventory"
                        className="p-2 text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft size={24} />
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">Categories & Groups</h1>
                </div>
                <p className="text-gray-600">
                    Organize your inventory items by creating and managing categories and groups.
                </p>
            </header>

            {/* Categories Section */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
                    <button
                        onClick={() => setIsCategoryModalOpen(true)}
                        className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 flex items-center justify-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add Category
                    </button>
                </div>

                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Groups
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {categories.map((category) => (
                                    <tr key={category.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{category.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500">
                                                {groups.filter(g => g.inventory_category_id === category.id).length} groups
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => {
                                                    setSelectedCategory(category);
                                                    setIsEditCategoryModalOpen(true);
                                                }}
                                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedCategory(category);
                                                    setIsDeleteCategoryModalOpen(true);
                                                }}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {categories.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No categories found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Groups Section */}
            <div className="space-y-4 mt-8">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">Groups</h2>
                    <button
                        onClick={() => setIsGroupModalOpen(true)}
                        className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 flex items-center justify-center gap-2"
                        disabled={categories.length === 0}
                    >
                        <Plus className="w-4 h-4" />
                        Add Group
                    </button>
                </div>

                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {groups.map((group) => (
                                    <tr key={group.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{group.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500">
                                                {group.category?.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => {
                                                    setSelectedGroup(group);
                                                    setIsEditGroupModalOpen(true);
                                                }}
                                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedGroup(group);
                                                    setIsDeleteGroupModalOpen(true);
                                                }}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {groups.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No groups found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <CategoryModal
                isOpen={isCategoryModalOpen}
                onClose={() => setIsCategoryModalOpen(false)}
                onSubmit={handleCreateCategory}
                title="Add Category"
            />

            <CategoryModal
                isOpen={isEditCategoryModalOpen}
                onClose={() => {
                    setIsEditCategoryModalOpen(false);
                    setSelectedCategory(null);
                }}
                onSubmit={handleUpdateCategory}
                initialName={selectedCategory?.name}
                title="Edit Category"
            />

            <GroupModal
                isOpen={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                onSubmit={handleCreateGroup}
                categories={categories}
                title="Add Group"
            />

            <GroupModal
                isOpen={isEditGroupModalOpen}
                onClose={() => {
                    setIsEditGroupModalOpen(false);
                    setSelectedGroup(null);
                }}
                onSubmit={handleUpdateGroup}
                categories={categories}
                initialData={selectedGroup ? {
                    categoryId: selectedGroup.inventory_category_id,
                    name: selectedGroup.name
                } : undefined}
                title="Edit Group"
            />

            <DeleteConfirmationModal
                isOpen={isDeleteCategoryModalOpen}
                onClose={() => {
                    setIsDeleteCategoryModalOpen(false);
                    setSelectedCategory(null);
                }}
                onConfirm={handleDeleteCategory}
                itemType="Category"
                itemName={selectedCategory?.name || ''}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteGroupModalOpen}
                onClose={() => {
                    setIsDeleteGroupModalOpen(false);
                    setSelectedGroup(null);
                }}
                onConfirm={handleDeleteGroup}
                itemType="Group"
                itemName={selectedGroup?.name || ''}
            />
        </div>
    );
}
